import type { SceneNode } from "@/document/model/nodes";
import { nodeRotationOrigin } from "@/document/operations/nodeWorldTransform";
import {
  applyAffinePoint,
  affineEquals,
  affineRotation,
  IDENTITY_AFFINE,
  multiplyAffine,
  type Affine2D,
} from "@/lib/math/affine";
import type { Rect } from "@/lib/math/rect";
import { rectCenter } from "@/lib/math/rotation";
import type { DisplayItem, DisplayPaint } from "./displayList";
import { evenOddContourTriangles, packMesh } from "./tessellation/meshKernels";
import { roundedRectClipPolygon } from "./tessellation/pathNodes";

export function composeDisplayWorldTransform(parentWorld: Affine2D, node: SceneNode): Affine2D {
  return multiplyAffine(parentWorld, nodeDisplayMatrix(node));
}

export function nodeDisplayMatrix(node: SceneNode): Affine2D {
  const rotation = node.transform.rotation ?? 0;
  if (!Number.isFinite(rotation) || rotation === 0) {
    return IDENTITY_AFFINE;
  }

  return affineRotation(rotation, nodeRotationOrigin(node));
}

export function applyDisplayWorldTransform(items: DisplayItem[], worldTransform: Affine2D): DisplayItem[] {
  if (affineEquals(worldTransform, IDENTITY_AFFINE)) {
    return items;
  }

  return items.map(item => finalizeDisplayItemWorldSpace(item, worldTransform));
}

export function finalizeDisplayItemWorldSpace(item: DisplayItem, worldTransform: Affine2D): DisplayItem {
  if (affineEquals(worldTransform, IDENTITY_AFFINE)) {
    return item;
  }

  if (item.kind === "text") {
    return {
      ...item,
      worldTransform,
      rotation: undefined,
      rotationOrigin: undefined,
    };
  }

  if (item.fill.type === "image") {
    const localVertices = displayItemSourceVertices(item);
    const worldVertices = transformVertexPairs(localVertices, worldTransform);
    return {
      ...item,
      kind: item.kind === "lines" ? "lines" : "triangles",
      cornerRadii: undefined,
      rotation: undefined,
      rotationOrigin: undefined,
      worldTransform,
      visualBounds: boundsFromVertices(worldVertices),
      shadowContours: item.shadowContours?.map(contour =>
        contour.map(point => applyAffinePoint(worldTransform, point)),
      ),
      vertices: localVertices,
    };
  }

  if (item.kind === "ellipse" || item.kind === "roundedRect") {
    // Bake solid SDF shapes into world-space triangles. Keeping roundedRect/ellipse
    // + worldTransform forced a Canvas2D raster capped at 1 doc-px/texel, which
    // upscales soft and pixelates under zoom×DPR (herringbone rotated planks).
    const localVertices = displayItemSourceVertices(item);
    const vertices = transformVertexPairs(localVertices, worldTransform);
    const visualBounds = boundsFromVertices(vertices);
    const fill = transformDisplayPaint(item.fill, worldTransform);
    return {
      ...item,
      kind: "triangles",
      cornerRadii: undefined,
      rotation: undefined,
      rotationOrigin: undefined,
      worldTransform: undefined,
      visualBounds,
      localVertices,
      localFill: item.fill,
      localShadowContours: item.shadowContours,
      shadowContours: item.shadowContours?.map(contour =>
        contour.map(point => applyAffinePoint(worldTransform, point)),
      ),
      vertices,
      fill,
    };
  }

  const vertices = transformDisplayVertices(item, worldTransform);
  const visualBounds = boundsFromVertices(vertices);
  const fill = transformDisplayPaint(item.fill, worldTransform);
  const localVertices = displayItemSourceVertices(item);

  return {
    ...item,
    kind: item.kind === "lines" ? "lines" : "triangles",
    cornerRadii: undefined,
    rotation: undefined,
    rotationOrigin: undefined,
    visualBounds,
    localVertices,
    localFill: item.fill,
    localShadowContours: item.shadowContours,
    shadowContours: item.shadowContours?.map(contour =>
      contour.map(point => applyAffinePoint(worldTransform, point)),
    ),
    vertices,
    fill,
  };
}

export function transformDisplayVertices(item: DisplayItem, matrix: Affine2D): Float32Array {
  const source = displayItemSourceVertices(item);
  return transformVertexPairs(source, matrix);
}

export function displayItemTriangleVertices(item: DisplayItem): Float32Array {
  const source = displayItemSourceVertices(item);

  if (item.worldTransform) {
    return transformVertexPairs(source, item.worldTransform);
  }

  if (item.positionOffset) {
    const dx = item.positionOffset.x;
    const dy = item.positionOffset.y;
    if (dx !== 0 || dy !== 0) {
      const shifted = new Float32Array(source.length);
      for (let index = 0; index < source.length; index += 2) {
        shifted[index] = source[index]! + dx;
        shifted[index + 1] = source[index + 1]! + dy;
      }
      return shifted;
    }
  }

  return source;
}

export function debugTriangleColorForIndex(seed: string, triangleIndex: number): [number, number, number, number] {
  let hash = 2166136261;
  const key = `${seed}:${triangleIndex}`;
  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  const hue = (hash >>> 0) % 360;
  const radians = (hue * Math.PI) / 180;
  return [
    0.5 + 0.45 * Math.sin(radians),
    0.5 + 0.45 * Math.sin(radians + 2.0943951023931953),
    0.5 + 0.45 * Math.sin(radians + 4.188790204786391),
    1,
  ];
}

function displayItemSourceVertices(item: DisplayItem): Float32Array {
  if (item.kind === "ellipse") {
    return packMesh(triangulateEllipseFill(item.bounds));
  }

  if (item.kind === "roundedRect") {
    const radii = item.cornerRadii;
    if (radii && radii.some(radius => radius > 0)) {
      return evenOddContourTriangles([roundedRectClipPolygon(item.bounds, radii)]);
    }
    if (item.vertices.length === 0) {
      return packMesh(rectVertices(item.bounds));
    }
  }

  if (item.vertices.length > 0) {
    return item.vertices;
  }

  return packMesh(rectVertices(item.bounds));
}

function transformDisplayPaint(paint: DisplayPaint, matrix: Affine2D): DisplayPaint {
  if (paint.type === "linearGradient") {
    const start = applyAffinePoint(matrix, { x: paint.start[0], y: paint.start[1] });
    const end = applyAffinePoint(matrix, { x: paint.end[0], y: paint.end[1] });
    return { ...paint, start: [start.x, start.y], end: [end.x, end.y] };
  }

  if (paint.type === "radialGradient") {
    const center = applyAffinePoint(matrix, { x: paint.center[0], y: paint.center[1] });
    const scale = Math.hypot(matrix.a, matrix.b);
    return { ...paint, center: [center.x, center.y], radius: paint.radius * scale };
  }

  if (paint.type === "image") {
    const rect = paint.imageRect;
    return {
      ...paint,
      imageRect: boundsFromVertices(transformVertexPairs(rectVertices(rect), matrix)),
    };
  }

  return paint;
}

function transformVertexPairs(vertices: ArrayLike<number>, matrix: Affine2D): Float32Array {
  const transformed = new Float32Array(vertices.length);
  for (let index = 0; index < vertices.length; index += 2) {
    const point = applyAffinePoint(matrix, { x: vertices[index]!, y: vertices[index + 1]! });
    transformed[index] = point.x;
    transformed[index + 1] = point.y;
  }
  return transformed;
}

function triangulateEllipseFill(bounds: Rect): number[] {
  const segments = ellipseSegmentCount(bounds);
  const center = rectCenter(bounds);
  const points = ellipsePoints(bounds, segments);
  const vertices: number[] = [];
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index]!;
    const next = points[(index + 1) % points.length]!;
    vertices.push(center.x, center.y, current.x, current.y, next.x, next.y);
  }
  return vertices;
}

function ellipsePoints(bounds: Rect, segments: number): Array<{ x: number; y: number }> {
  const centerX = bounds.x + bounds.width / 2;
  const centerY = bounds.y + bounds.height / 2;
  const radiusX = Math.abs(bounds.width / 2);
  const radiusY = Math.abs(bounds.height / 2);
  const points: Array<{ x: number; y: number }> = [];
  for (let index = 0; index < segments; index += 1) {
    const angle = (index / segments) * Math.PI * 2;
    points.push({
      x: centerX + Math.cos(angle) * radiusX,
      y: centerY + Math.sin(angle) * radiusY,
    });
  }
  return points;
}

function ellipseSegmentCount(bounds: { width: number; height: number }): number {
  const radius = Math.max(Math.abs(bounds.width), Math.abs(bounds.height)) / 2;
  return Math.max(12, Math.min(72, Math.ceil(radius / 4)));
}

function rectVertices(rect: Rect): number[] {
  return [
    rect.x,
    rect.y,
    rect.x + rect.width,
    rect.y,
    rect.x,
    rect.y + rect.height,
    rect.x,
    rect.y + rect.height,
    rect.x + rect.width,
    rect.y,
    rect.x + rect.width,
    rect.y + rect.height,
  ];
}

function boundsFromVertices(vertices: ArrayLike<number>): Rect {
  if (vertices.length < 2) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let left = vertices[0]!;
  let top = vertices[1]!;
  let right = left;
  let bottom = top;
  for (let index = 2; index < vertices.length; index += 2) {
    const x = vertices[index]!;
    const y = vertices[index + 1]!;
    left = Math.min(left, x);
    top = Math.min(top, y);
    right = Math.max(right, x);
    bottom = Math.max(bottom, y);
  }
  return { x: left, y: top, width: right - left, height: bottom - top };
}

export function applyCanvasAffineTransform(context: CanvasRenderingContext2D, matrix: Affine2D): void {
  context.transform(matrix.a, matrix.b, matrix.c, matrix.d, matrix.e, matrix.f);
}
